/**
 * PWA（ホーム画面追加・スタンドアロン起動）まわりの判定ユーティリティ。
 *
 * すべて副作用のない純粋関数にしてある。
 * 「Service Worker を登録してよいか」「いま standalone か」「どの経路で接続しているか」を
 * ここに集約し、フック・コンポーネント側は判定ロジックを持たない。
 */

/**
 * 接続経路の分類。
 *
 * MedTube は院内 NAS 上で動き、ブラウザからは Tailscale 名 / mDNS(.local) / LAN IP /
 * localhost のいずれかで到達する（caddy/README.md）。
 * サーバに届かないときの案内文はこの経路で出し分ける。
 * Tailscale の帯は backend の getExternalFixedCidrs() と定義を揃えている。
 */
export type AccessRoute = 'tailscale' | 'local' | 'unknown';

/** Tailscale の IPv6 ULA プレフィックス（fd7a:115c:a1e0::/48） */
const TAILSCALE_IPV6_PREFIX = 'fd7a:115c:a1e0';

/** IPv4 ドット記法にマッチするか（各オクテットが 1〜3 桁の数字） */
const IPV4_PATTERN = /^(\d{1,3})\.(\d{1,3})\.(\d{1,3})\.(\d{1,3})$/;

/**
 * ホスト名から接続経路を判定する。
 *
 * URL の hostname は IPv6 のとき `[::1]` の形で来ることがあるため角括弧を外す。
 * 判定できないものは 'unknown'（＝汎用のネットワーク案内）に倒す。
 */
export const classifyAccessRoute = (rawHostname: string): AccessRoute => {
  const hostname = (rawHostname || '').toLowerCase().replace(/^\[|\]$/g, '');
  if (!hostname) return 'unknown';

  if (hostname === 'localhost' || hostname === '::1') return 'local';
  if (hostname.endsWith('.ts.net')) return 'tailscale';
  if (hostname.endsWith('.local')) return 'local';
  if (hostname.startsWith(TAILSCALE_IPV6_PREFIX)) return 'tailscale';

  const matched = hostname.match(IPV4_PATTERN);
  if (matched) {
    const first = Number(matched[1]);
    const second = Number(matched[2]);
    // 100.64.0.0/10（RFC6598 CGNAT）= Tailscale が配る IP
    if (first === 100 && second >= 64 && second <= 127) return 'tailscale';
    if (first === 127 || first === 10) return 'local';
    if (first === 192 && second === 168) return 'local';
    if (first === 172 && second >= 16 && second <= 31) return 'local';
  }

  return 'unknown';
};

/**
 * PWA として（ホーム画面から）スタンドアロン起動しているか。
 *
 * iOS Safari は display-mode メディアクエリを長く実装しなかったため、
 * 独自の navigator.standalone も併せて見る。
 */
export const isStandalone = (): boolean => {
  if (typeof window === 'undefined') return false;
  const byDisplayMode = window.matchMedia?.('(display-mode: standalone)').matches ?? false;
  const byIOSFlag =
    (window.navigator as Navigator & { standalone?: boolean }).standalone === true;
  return byDisplayMode || byIOSFlag;
};

/** Service Worker API が使える環境か。 */
export const isServiceWorkerSupported = (): boolean =>
  typeof navigator !== 'undefined' && 'serviceWorker' in navigator;

/**
 * Service Worker がナビゲーション応答を貯めるキャッシュ名の接頭辞。
 * public/sw.js の `${CACHE_PREFIX}-pages-${BUILD_ID}` と一致させること。
 */
const PAGE_CACHE_PREFIX = 'medtube-pages-';

/**
 * 閲覧履歴に相当するキャッシュを削除する（ログアウト時に呼ぶ）。
 *
 * ナビゲーションキャッシュの**キーはリクエスト URL そのもの**で、検索クエリ
 * （疾患名・術式名）や `/videos/:id` がそのまま残る。応答本文は index.html なので
 * 個人データは入らないが、共有端末では「前の利用者が何を見たか」が読み取れてしまう。
 * Cache Storage は「閲覧履歴の削除」では消えないため、ログアウトで明示的に消す。
 *
 * 失敗してもログアウト自体は妨げない（キャッシュ削除は付随処理）。
 */
export const clearBrowsingCaches = async (): Promise<void> => {
  if (typeof caches === 'undefined') return;
  try {
    const keys = await caches.keys();
    await Promise.all(
      keys.filter((key) => key.startsWith(PAGE_CACHE_PREFIX)).map((key) => caches.delete(key))
    );
  } catch {
    // ストレージにアクセスできない環境（プライベートウィンドウ等）では何もしない
  }
};

/** shouldRegisterServiceWorker の判定材料 */
export interface ServiceWorkerRegistrationCondition {
  /** Service Worker API が使えるか */
  isSupported: boolean;
  /** 本番ビルドか（dev サーバの非ハッシュ資産をキャッシュさせない） */
  isProduction: boolean;
  /** 静的デモモードか（Cloudflare Pages のデモ環境に SW を残さない） */
  isDemoMode: boolean;
}

/**
 * Service Worker を登録してよいかを判定する。
 *
 * 環境変数を直接読まず引数で受け取るのは、テストから全分岐を再現できるようにするため
 * （NODE_ENV を見て早期 return すると、テスト環境では常に無効になり本体が一切
 * カバーされない）。
 */
export const shouldRegisterServiceWorker = ({
  isSupported,
  isProduction,
  isDemoMode,
}: ServiceWorkerRegistrationCondition): boolean =>
  isSupported && isProduction && !isDemoMode;
